import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import {
  DmCard,
  DmCardBody,
  DmContentDivider,
  DmLabelSection,
  DmSelect,
  DmSelectItem,
  DmSwitch,
} from '@digitale-menschen/component-library-angular';

/** What this tab reports upwards; a subset of the user DTO. */
export interface BenachrichtigungenChange {
  readonly newsletterAktiv?: boolean;
  readonly newsletterPeriod?: number | null;
  readonly emailBenachrichtigungenAktiv?: boolean;
  readonly aufgabenBenachrichtigungenAktiv?: boolean;
}

/**
 * The Benachrichtigungen tab of the Benutzer drawer.
 *
 * Dumb by design: the form owns the model, this renders it and reports changes.
 *
 * **What the design shows and this does not.** The design lists four separate Aufgaben switches
 * («Zuweisung an Benutzer», «Fälligkeitsdatum überschritten», «Fälligkeitsdatum in 3 Tagen»,
 * «Aufgabenübersicht jeweils Montags») and two «Anforderungen» switches. The data model has one
 * column for all Aufgaben mail — `AufgabenBenachrichtigungenAktiv`, which
 * `NotificationPreferenceService` applies to all five Aufgaben notification types — and nothing
 * at all for Anforderungen. Rendering six switches that write one column would be a promise the
 * product cannot keep, so the Aufgaben group is one switch and the Anforderungen group is
 * absent. Per-type opt-outs need a preference table and a sender per type; agreed on
 * 2026-09-09 to map only the existing columns.
 */
@Component({
  selector: 'lp-benutzer-benachrichtigungen',
  imports: [DmCard, DmCardBody, DmContentDivider, DmLabelSection, DmSelect, DmSelectItem, DmSwitch],
  templateUrl: './benutzer-benachrichtigungen.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BenutzerBenachrichtigungenComponent {
  public readonly newsletterAktiv = input.required<boolean>();
  public readonly newsletterPeriod = input.required<number | null | undefined>();
  public readonly emailBenachrichtigungenAktiv = input.required<boolean>();
  public readonly aufgabenBenachrichtigungenAktiv = input.required<boolean>();
  public readonly disabled = input<boolean>(false);

  public readonly changed = output<BenachrichtigungenChange>();

  /** The newsletter intervals the API accepts, in months. */
  protected readonly intervalle = [1, 3, 6] as const;

  /**
   * The interval select's accessible name.
   *
   * The visible «Newsletter Abonnieren» beside it is a row heading in its own grid column, and
   * it names the *group* — the switch and this select together. Without this the select is
   * announced as unnamed, the same reason the create-edit form labels its controls this way.
   */
  protected readonly newsletterIntervallAriaLabel = $localize`:@@benutzerverwaltung.benachrichtigungen.newsletterIntervall:Newsletter Intervall`;

  /**
   * Switches the newsletter subscription.
   *
   * Turning it on with no interval yet picks the shortest one, so the select is never left
   * empty beside a ticked switch — a state the API would store as «subscribed, never sent».
   *
   * @param {boolean} aktiv - The new state.
   */
  protected onNewsletterAktivChange(aktiv: boolean): void {
    this.changed.emit({
      newsletterAktiv: aktiv,
      newsletterPeriod: aktiv ? (this.newsletterPeriod() ?? this.intervalle[0]) : null,
    });
  }

  /**
   * Picks the newsletter interval.
   *
   * @param {string} value - The select's value, in months.
   */
  protected onNewsletterPeriodChange(value: string): void {
    const months = Number(value);
    if (!this.intervalle.includes(<(typeof this.intervalle)[number]>months)) {
      return;
    }
    this.changed.emit({ newsletterPeriod: months });
  }

  /**
   * The label for an interval.
   *
   * @param {number} months - The interval in months.
   * @returns {string} «Jeden Monat» or «Alle N Monate».
   */
  protected intervallLabel(months: number): string {
    return months === 1
      ? $localize`:@@benutzerverwaltung.newsletter.jedenMonat:Jeden Monat`
      : $localize`:@@benutzerverwaltung.newsletter.alleMonate:Alle ${months}:months: Monate`;
  }
}
